import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, Pencil, Trash2, MessageSquare } from 'lucide-react';
import api from '@/lib/api';
import { formatDateShort } from '@/lib/utils';
import { useUIStore } from '@/stores/ui-store';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Card from '@/components/ui/Card';
import Modal from '@/components/ui/Modal';
import Textarea from '@/components/ui/Textarea';
import Input from '@/components/ui/Input';
import RatingStars from '@/components/ui/RatingStars';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import Pagination from '@/components/ui/Pagination';

interface Review {
  id: string;
  product_id: string;
  product_name: string;
  product_slug: string;
  product_image: string | null;
  rating: number;
  title: string;
  body: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

function useUserReviews(page: number) {
  return useQuery({
    queryKey: ['user-reviews', page],
    queryFn: () =>
      api.get<{ reviews: Review[]; total: number }>(
        `/user/reviews?page=${page}`,
      ),
  });
}

const REVIEWS_PER_PAGE = 10;

const STATUS_VARIANTS: Record<string, 'green' | 'yellow' | 'red' | 'gray'> = {
  pending: 'yellow',
  approved: 'green',
  rejected: 'red',
};

export default function Reviews() {
  const { t } = useTranslation('account');
  const queryClient = useQueryClient();
  const addToast = useUIStore((s) => s.addToast);

  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useUserReviews(page);

  const reviews = data?.reviews ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / REVIEWS_PER_PAGE);

  // Edit modal state
  const [editingReview, setEditingReview] = useState<Review | null>(null);
  const [editRating, setEditRating] = useState(5);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Delete modal state
  const [deletingReview, setDeletingReview] = useState<Review | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  function openEditModal(review: Review) {
    setEditingReview(review);
    setEditRating(review.rating);
    setEditTitle(review.title);
    setEditBody(review.body);
    setEditErrors({});
  }

  function closeEditModal() {
    setEditingReview(null);
    setEditRating(5);
    setEditTitle('');
    setEditBody('');
    setEditErrors({});
  }

  async function handleSaveEdit() {
    const newErrors: Record<string, string> = {};
    if (!editTitle.trim()) newErrors.title = t('reviews.errors.titleRequired');
    if (!editBody.trim()) newErrors.body = t('reviews.errors.bodyRequired');
    if (editRating < 1 || editRating > 5) newErrors.rating = t('reviews.errors.ratingRange');
    setEditErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (!editingReview) return;
    setIsSavingEdit(true);
    try {
      await api.put(`/user/reviews/${editingReview.id}`, {
        rating: editRating,
        title: editTitle.trim(),
        body: editBody.trim(),
      });
      addToast(t('reviews.toasts.updated'), 'success');
      queryClient.invalidateQueries({ queryKey: ['user-reviews'] });
      closeEditModal();
    } catch (err: any) {
      addToast(err.message || t('reviews.toasts.updateFailed'), 'error');
    } finally {
      setIsSavingEdit(false);
    }
  }

  async function handleDelete() {
    if (!deletingReview) return;
    setIsDeleting(true);
    try {
      await api.delete(`/user/reviews/${deletingReview.id}`);
      addToast(t('reviews.toasts.deleted'), 'success');
      queryClient.invalidateQueries({ queryKey: ['user-reviews'] });
      setDeletingReview(null);
    } catch (err: any) {
      addToast(err.message || t('reviews.toasts.deleteFailed'), 'error');
    } finally {
      setIsDeleting(false);
    }
  }

  if (isLoading) {
    return <LoadingSpinner className="py-16" />;
  }

  if (isError) {
    return (
      <div className="text-center py-16">
        <p className="text-ink-muted mb-4">{t('reviews.errorLoading')}</p>
        <Button variant="outline" onClick={() => window.location.reload()}>
          {t('common.tryAgain')}
        </Button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-display font-bold text-ink">
          {t('reviews.heading')}
        </h2>
        {total > 0 && (
          <p className="text-sm text-ink-muted">
            {t('reviews.totalCount', { count: total })}
          </p>
        )}
      </div>

      {reviews.length === 0 ? (
        <div className="text-center py-16">
          <MessageSquare
            className="h-16 w-16 text-ink-muted mx-auto mb-4"
            aria-hidden="true"
          />
          <h3 className="text-lg font-semibold text-ink mb-2">
            {t('reviews.emptyHeading')}
          </h3>
          <p className="text-ink-muted mb-6">{t('reviews.emptyDescription')}</p>
          <Link to="/account/orders">
            <Button variant="outline">{t('reviews.viewPastOrders')}</Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => {
            const variant = STATUS_VARIANTS[review.status] ?? 'gray';
            const statusLabel = t(`reviews.status.${review.status}`, { defaultValue: review.status });

            return (
              <Card key={review.id}>
                <div className="flex gap-4">
                  {/* Product image */}
                  {review.product_image ? (
                    <Link
                      to={`/product/${review.product_slug}`}
                      className="shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
                    >
                      <img
                        src={review.product_image}
                        alt={review.product_name}
                        className="w-16 h-16 object-cover rounded-md border border-line"
                        loading="lazy"
                      />
                    </Link>
                  ) : (
                    <div className="w-16 h-16 bg-surface-muted rounded-md flex items-center justify-center shrink-0 border border-line">
                      <Star
                        className="h-6 w-6 text-ink-muted"
                        aria-hidden="true"
                      />
                    </div>
                  )}

                  {/* Review content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                      <div>
                        <Link
                          to={`/product/${review.product_slug}`}
                          className="font-medium text-ink hover:text-sage-700 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-500 rounded"
                        >
                          {review.product_name}
                        </Link>
                        <div className="flex items-center gap-2 mt-1">
                          <RatingStars rating={review.rating} size="sm" />
                          <Badge variant={variant}>{statusLabel}</Badge>
                        </div>
                      </div>
                      <span className="text-xs text-ink-muted shrink-0">
                        {formatDateShort(review.created_at)}
                      </span>
                    </div>

                    {review.title && (
                      <p className="font-medium text-sm text-ink mt-2">
                        {review.title}
                      </p>
                    )}
                    <p className="text-sm text-ink-soft mt-1 line-clamp-3">
                      {review.body}
                    </p>

                    {/* Actions */}
                    <div className="flex gap-2 mt-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(review)}
                        aria-label={t('reviews.editAriaLabel', { name: review.product_name })}
                      >
                        <Pencil
                          className="h-3.5 w-3.5"
                          aria-hidden="true"
                        />
                        {t('reviews.edit')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeletingReview(review)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        aria-label={t('reviews.deleteAriaLabel', { name: review.product_name })}
                      >
                        <Trash2
                          className="h-3.5 w-3.5"
                          aria-hidden="true"
                        />
                        {t('reviews.delete')}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}

          {totalPages > 1 && (
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              className="mt-6 justify-center"
            />
          )}
        </div>
      )}

      {/* Edit Review Modal */}
      <Modal
        isOpen={!!editingReview}
        onClose={closeEditModal}
        title={t('reviews.editModal.title')}
      >
        {editingReview && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSaveEdit();
            }}
            noValidate
          >
            <p className="text-sm text-ink-muted mb-4">
              {t('reviews.editModal.reviewing')}{' '}
              <span className="font-medium text-ink-soft">
                {editingReview.product_name}
              </span>
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">
                  {t('reviews.editModal.ratingLabel')}
                </label>
                <RatingStars
                  rating={editRating}
                  interactive
                  onChange={setEditRating}
                  size="lg"
                />
                {editErrors.rating && (
                  <p className="mt-1 text-sm text-red-600" role="alert">
                    {editErrors.rating}
                  </p>
                )}
              </div>

              <Input
                label={t('reviews.editModal.titleLabel')}
                value={editTitle}
                onChange={(e) => {
                  setEditTitle(e.target.value);
                  if (editErrors.title) {
                    setEditErrors((prev) => {
                      const next = { ...prev };
                      delete next.title;
                      return next;
                    });
                  }
                }}
                error={editErrors.title}
                required
                placeholder={t('reviews.editModal.titlePlaceholder')}
              />

              <Textarea
                label={t('reviews.editModal.bodyLabel')}
                value={editBody}
                onChange={(e) => {
                  setEditBody(e.target.value);
                  if (editErrors.body) {
                    setEditErrors((prev) => {
                      const next = { ...prev };
                      delete next.body;
                      return next;
                    });
                  }
                }}
                error={editErrors.body}
                required
                placeholder={t('reviews.editModal.bodyPlaceholder')}
                rows={4}
              />
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-line">
              <Button type="button" variant="ghost" onClick={closeEditModal}>
                {t('reviews.editModal.cancel')}
              </Button>
              <Button type="submit" isLoading={isSavingEdit}>
                {t('reviews.editModal.save')}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deletingReview}
        onClose={() => setDeletingReview(null)}
        title={t('reviews.deleteModal.title')}
      >
        <p className="text-sm text-ink-soft mb-6">
          {t('reviews.deleteModal.bodyPrefix')}{' '}
          <span className="font-semibold">
            {deletingReview?.product_name}
          </span>
          {t('reviews.deleteModal.bodySuffix')}
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeletingReview(null)}>
            {t('reviews.deleteModal.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            isLoading={isDeleting}
          >
            {t('reviews.deleteModal.confirm')}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
